import React, { useEffect, useMemo, useState } from 'react'
import { BadgeCheck, Ban, Loader2, MailX, Search, Trash2, UserCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiErrorMessage, communicationsService } from '../../services/communicationsService'
import type {
  MailSuppression,
  NewsletterContact,
  NewsletterSubscription
} from '../../types/communications'

interface Props {
  notify?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const NewsletterContacts: React.FC<Props> = ({ notify }) => {
  const { t } = useTranslation()
  const [subscriptions, setSubscriptions] = useState<NewsletterSubscription[]>([])
  const [suppressions, setSuppressions] = useState<MailSuppression[]>([])
  const [contacts, setContacts] = useState<NewsletterContact[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const [subscriptionRows, suppressionRows, contactRows] = await Promise.all([
        communicationsService.listSubscriptions(),
        communicationsService.listSuppressions(),
        communicationsService.listContacts()
      ])
      setSubscriptions(subscriptionRows)
      setSuppressions(suppressionRows)
      setContacts(contactRows)
    } catch (error) {
      notify?.(apiErrorMessage(error, t('communications.newsletters.contactsLoadFailed')), 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const term = search.trim().toLowerCase()
  const matches = (value?: string | null) => !term || String(value || '').toLowerCase().includes(term)
  const visibleSubscriptions = useMemo(
    () => subscriptions.filter(row => matches(row.email)),
    [subscriptions, term]
  )
  const visibleContacts = useMemo(
    () => contacts.filter(row => matches(row.email) || matches(row.fullName) || matches(row.organisation)),
    [contacts, term]
  )
  const visibleSuppressions = useMemo(
    () => suppressions.filter(row => matches(row.email)),
    [suppressions, term]
  )

  const act = async (key: string, action: () => Promise<unknown>, message: string) => {
    setBusy(key)
    try {
      await action()
      await load()
      notify?.(message, 'success')
    } catch (error) {
      notify?.(apiErrorMessage(error, t('communications.newsletters.actionFailed')), 'error')
    } finally {
      setBusy('')
    }
  }

  if (loading) return <div className="flex h-48 items-center justify-center"><Loader2 className="animate-spin text-blue-600" /></div>

  return <div className="space-y-4">
    <label className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2">
      <Search size={14} className="text-gray-400" />
      <input value={search} onChange={event => setSearch(event.target.value)}
        placeholder={t('communications.newsletters.searchContacts')}
        className="w-full text-sm outline-none" />
    </label>

    <section className="rounded-xl border border-gray-200 bg-white">
      <header className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
        <UserCheck size={14} className="text-blue-600" />
        <h4 className="text-sm font-semibold">{t('communications.newsletters.consent')}</h4>
        <span className="ml-auto text-[11px] text-gray-500">
          {t('communications.newsletters.rowCount', { count: visibleSubscriptions.length })}
        </span>
      </header>
      <div className="max-h-72 overflow-y-auto">
        {!visibleSubscriptions.length && <p className="p-5 text-center text-xs text-gray-400">
          {t('communications.newsletters.noSubscriptions')}
        </p>}
        {visibleSubscriptions.map(row => <div key={row.email}
          className="flex flex-wrap items-center gap-3 border-b border-gray-50 px-4 py-2 text-xs last:border-0">
          <span className="min-w-0 flex-1 truncate font-medium text-gray-800">{row.email}</span>
          <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase">{row.locale}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
            row.consent_status === 'SUBSCRIBED' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          }`}>
            {t(`communications.newsletters.consentStatus.${row.consent_status}`)}
          </span>
          <button disabled={Boolean(busy)}
            onClick={() => act(`sub-${row.email}`, () => communicationsService.setSubscription(
              row.email,
              row.consent_status === 'SUBSCRIBED' ? 'UNSUBSCRIBED' : 'SUBSCRIBED',
              row.locale
            ), t('communications.newsletters.consentUpdated'))}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-[11px] font-medium disabled:opacity-50">
            {busy === `sub-${row.email}` ? <Loader2 size={11} className="animate-spin" /> : <MailX size={11} />}
            {t(row.consent_status === 'SUBSCRIBED'
              ? 'communications.newsletters.unsubscribeAction'
              : 'communications.newsletters.resubscribeAction')}
          </button>
        </div>)}
      </div>
    </section>

    <section className="rounded-xl border border-gray-200 bg-white">
      <header className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3">
        <BadgeCheck size={14} className="text-blue-600" />
        <h4 className="text-sm font-semibold">{t('communications.newsletters.approvals')}</h4>
        <p className="w-full text-[11px] text-gray-500 sm:w-auto">{t('communications.newsletters.approvalsHint')}</p>
      </header>
      <div className="max-h-72 overflow-y-auto">
        {!visibleContacts.length && <p className="p-5 text-center text-xs text-gray-400">
          {t('communications.newsletters.noContacts')}
        </p>}
        {visibleContacts.map(row => {
          const key = `${row.sourceType}-${row.sourceId}-${row.email}`
          return <div key={key} className="flex flex-wrap items-center gap-3 border-b border-gray-50 px-4 py-2 text-xs last:border-0">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-gray-800">{row.fullName || row.email}</span>
              <span className="block truncate text-[10px] text-gray-500">{row.email}{row.organisation ? ` · ${row.organisation}` : ''}</span>
            </span>
            <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase">
              {t(`communications.newsletters.contactType.${row.sourceType}`)}
            </span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
              row.approved ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {t(row.approved ? 'communications.newsletters.approved' : 'communications.newsletters.notApproved')}
            </span>
            <button disabled={Boolean(busy)}
              onClick={() => act(key, () => row.approved
                ? communicationsService.revokeContact(row.sourceType, row.sourceId, row.email)
                : communicationsService.approveContact(row.sourceType, row.sourceId, row.email, row.locale),
              t('communications.newsletters.approvalUpdated'))}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-[11px] font-medium disabled:opacity-50">
              {busy === key ? <Loader2 size={11} className="animate-spin" /> : <Ban size={11} />}
              {t(row.approved ? 'communications.newsletters.revoke' : 'communications.newsletters.approve')}
            </button>
          </div>
        })}
      </div>
    </section>

    <section className="rounded-xl border border-gray-200 bg-white">
      <header className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3">
        <Ban size={14} className="text-red-600" />
        <h4 className="text-sm font-semibold">{t('communications.newsletters.suppressions')}</h4>
        <p className="w-full text-[11px] text-gray-500 sm:w-auto">{t('communications.newsletters.suppressionsHint')}</p>
      </header>
      <div className="max-h-60 overflow-y-auto">
        {!visibleSuppressions.length && <p className="p-5 text-center text-xs text-gray-400">
          {t('communications.newsletters.noSuppressions')}
        </p>}
        {visibleSuppressions.map(row => <div key={row.email}
          className="flex flex-wrap items-center gap-3 border-b border-gray-50 px-4 py-2 text-xs last:border-0">
          <span className="min-w-0 flex-1 truncate font-medium text-gray-800">{row.email}</span>
          <span className="rounded bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700">{row.reason}</span>
          <span className="text-[10px] text-gray-400">{row.created_at ? new Date(row.created_at).toLocaleDateString() : ''}</span>
          <button disabled={Boolean(busy)}
            onClick={() => act(`sup-${row.email}`, () => communicationsService.removeSuppression(row.email),
              t('communications.newsletters.suppressionRemoved'))}
            className="rounded-lg border border-gray-300 p-1 text-gray-500 hover:text-red-600 disabled:opacity-50">
            {busy === `sup-${row.email}` ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
          </button>
        </div>)}
      </div>
    </section>
  </div>
}

export default NewsletterContacts
